import { c as _c } from "react/compiler-runtime";
/**
 * Skeleton — shared content-skeleton primitives for the marketing site.
 *
 * Uses the `.cy-skeleton` shimmer defined in app/globals.css. Pass either
 * a `variant` (line / line-sm / line-lg / circle / block) or arbitrary
 * className + style for one-off shapes.
 */

import type { CSSProperties } from "react";
type SkeletonProps = {
  className?: string;
  lines?: number;
  style?: CSSProperties;
  variant?: "block" | "line" | "line-sm" | "line-lg" | "circle";
};
const VARIANT_CLASS: Record<NonNullable<SkeletonProps["variant"]>, string> = {
  block: "",
  line: "cy-skeleton-line",
  "line-sm": "cy-skeleton-line-sm",
  "line-lg": "cy-skeleton-line-lg",
  circle: "cy-skeleton-circle"
};
export default function Skeleton(t0) {
  const $ = _c(12);
  const {
    className: t1,
    lines,
    style,
    variant: t2
  } = t0;
  const className = t1 === undefined ? "" : t1;
  const variant = t2 === undefined ? "block" : t2;
  if (lines && lines > 1) {
    let t3;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t3 = {
        display: "flex",
        flexDirection: "column",
        gap: 8
      };
      $[0] = t3;
    } else {
      t3 = $[0];
    }
    let t4;
    if ($[1] !== lines) {
      t4 = Array.from({
        length: lines
      });
      $[1] = lines;
      $[2] = t4;
    } else {
      t4 = $[2];
    }
    let t5;
    if ($[3] !== lines || $[4] !== t4) {
      t5 = t4.map((_, i) => <div key={i} className={`cy-skeleton ${VARIANT_CLASS.line}`} style={{
        width: i === lines - 1 ? "65%" : "100%"
      }} />);
      $[3] = lines;
      $[4] = t4;
      $[5] = t5;
    } else {
      t5 = $[5];
    }
    let t6;
    if ($[6] !== className || $[7] !== t5) {
      t6 = <div className={className} style={t3}>{t5}</div>;
      $[6] = className;
      $[7] = t5;
      $[8] = t6;
    } else {
      t6 = $[8];
    }
    return t6;
  }
  const t3 = `cy-skeleton ${VARIANT_CLASS[variant]} ${className}`;
  let t4;
  if ($[9] !== style || $[10] !== t3) {
    t4 = <div className={t3} style={style} />;
    $[9] = style;
    $[10] = t3;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  return t4;
}

/**
 * Card-shaped skeleton (used for blog/portfolio/service cards in
 * route loading.tsx files). Wraps the shimmer in the same border +
 * panel chrome the real cards use so the page doesn't shift.
 */
export function SkeletonCard(t0) {
  const $ = _c(9);
  const {
    imageHeight: t1,
    lines: t2
  } = t0;
  const imageHeight = t1 === undefined ? 192 : t1;
  const lines = t2 === undefined ? 3 : t2;
  let t3;
  if ($[0] !== imageHeight) {
    t3 = <Skeleton variant="block" style={{
      width: "100%",
      height: imageHeight,
      borderRadius: 0
    }} />;
    $[0] = imageHeight;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  let t4;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <Skeleton variant="line-sm" style={{
      width: "30%"
    }} />;
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  let t5;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <Skeleton variant="line-lg" style={{
      width: "75%"
    }} />;
    $[3] = t5;
  } else {
    t5 = $[3];
  }
  let t6;
  if ($[4] !== lines) {
    t6 = <div className="space-y-3 p-6">{t4}{t5}<div className="pt-1"><Skeleton lines={lines} /></div></div>;
    $[4] = lines;
    $[5] = t6;
  } else {
    t6 = $[5];
  }
  let t7;
  if ($[6] !== t3 || $[7] !== t6) {
    t7 = <div className="overflow-hidden rounded-2xl border border-[var(--stroke)] bg-[var(--panel)]/55">{t3}{t6}</div>;
    $[6] = t3;
    $[7] = t6;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  return t7;
}